import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, LogOut, Search, Pencil, Check, X, Settings, User, Palette, Archive, Building2, Globe, ArrowLeft, AlertTriangle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

function WorktopsDashboard() {
  const navigate = useNavigate();
  const { token, API, logout, user, companyContext } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingNickname, setEditingNickname] = useState(null);
  const [nicknameValue, setNicknameValue] = useState('');
  const [staleQuotes, setStaleQuotes] = useState([]);
  const [showStaleDialog, setShowStaleDialog] = useState(false);
  const [selectedStale, setSelectedStale] = useState({});
  const [archiving, setArchiving] = useState(false);
  const [thresholdDays, setThresholdDays] = useState(30);
  
  // Check if user has access to memorials dashboard (can go back)
  const hasMemorials = companyContext?.features?.memorials_enabled !== false;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin';
  
  useEffect(() => {
    fetchOrders();
    if (isAdmin) {
      checkStaleQuotes();
    }
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/worktops`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch worktop orders');
    } finally {
      setLoading(false);
    }
  };

  const checkStaleQuotes = async () => {
    try {
      const dismissed = sessionStorage.getItem('stale_quotes_dismissed');
      if (dismissed) return;
      
      const response = await axios.get(`${API}/worktops/stale-quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.stale_quotes?.length > 0) {
        setStaleQuotes(response.data.stale_quotes);
        setThresholdDays(response.data.threshold_days);
        setShowStaleDialog(true);
      }
    } catch (error) {
      console.error('Failed to check stale quotes:', error);
    }
  };

  const handleBulkArchive = async () => {
    const idsToArchive = Object.entries(selectedStale)
      .filter(([_, checked]) => checked)
      .map(([id]) => id);
    
    if (idsToArchive.length === 0) {
      toast.error('Please select at least one quote to archive');
      return;
    }
    
    setArchiving(true);
    try {
      await axios.post(`${API}/worktops/bulk-archive`, 
        { order_ids: idsToArchive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${idsToArchive.length} quote${idsToArchive.length > 1 ? 's' : ''} archived`);
      setShowStaleDialog(false);
      sessionStorage.setItem('stale_quotes_dismissed', 'true');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to archive quotes');
    } finally {
      setArchiving(false);
    }
  };

  const dismissStaleDialog = () => {
    setShowStaleDialog(false);
    sessionStorage.setItem('stale_quotes_dismissed', 'true');
  };

  const toggleAllStale = (checked) => {
    const newSelected = {};
    staleQuotes.forEach(q => { newSelected[q.id] = checked; });
    setSelectedStale(newSelected);
  };

  const selectedCount = Object.values(selectedStale).filter(Boolean).length;

  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'completed', 'trade-requests', 'quotes', 'archived'

  const filteredOrders = orders.filter(order => {
    // Show only archived orders when archived filter is active
    if (statusFilter === 'archived') {
      if (!order.is_archived) {
        return false;
      }
    } else {
      // Exclude archived orders for all other filters
      if (order.is_archived) {
        return false;
      }
    }
    
    // Apply status filter
    if (statusFilter === 'active' && order.status === 'completed') {
      return false;
    }
    if (statusFilter === 'completed' && order.status !== 'completed') {
      return false;
    }
    if (statusFilter === 'trade-requests' && order.status !== 'Trade Quote Request') {
      return false;
    }
    if (statusFilter === 'drafts' && order.status !== 'draft') {
      return false;
    }
    // New Quote Requests: orders with quote/new_quote status AND no price set (total = 0 or null)
    if (statusFilter === 'quotes') {
      const needsQuoting = ['quote', 'new_quote'].includes(order.status) && (!order.total || order.total === 0);
      if (!needsQuoting) return false;
    }
    
    // Apply search filter
    return order.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           order.order_number.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-slate-200 text-slate-700 border-slate-300',
      'new_quote': 'bg-orange-100 text-orange-800 border-orange-300',
      'quote': 'bg-gray-100 text-gray-800',
      'Trade Quote Request': 'bg-amber-100 text-amber-800',
      'accepted': 'bg-blue-100 text-blue-800',
      'deposit-requested': 'bg-violet-100 text-violet-800',
      'deposit-taken': 'bg-green-100 text-green-800',
      'templating-booked': 'bg-purple-100 text-purple-800',
      'templating-done': 'bg-indigo-100 text-indigo-800',
      'stone-ordered': 'bg-yellow-100 text-yellow-800',
      'stone-fabricated': 'bg-orange-100 text-orange-800',
      'fitting-scheduled': 'bg-pink-100 text-pink-800',
      'completed': 'bg-emerald-100 text-emerald-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const startEditingNickname = (orderId, currentName) => {
    setEditingNickname(orderId);
    setNicknameValue(currentName);
  };

  const cancelEditingNickname = () => {
    setEditingNickname(null);
    setNicknameValue('');
  };

  const saveNickname = async (orderId) => {
    try {
      await axios.patch(`${API}/worktops/${orderId}/nickname`, 
        { display_name: nicknameValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Nickname updated');
      setEditingNickname(null);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update nickname');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading worktop orders...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      {/* Stale Quotes Archive Dialog */}
      <Dialog open={showStaleDialog} onOpenChange={setShowStaleDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Stale Quotes Found
            </DialogTitle>
            <DialogDescription>
              The following {staleQuotes.length} worktop quote{staleQuotes.length !== 1 ? 's are' : ' is'} older than {thresholdDays} days. Would you like to archive any of them?
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex items-center justify-between py-2 border-b">
            <label className="flex items-center gap-2 cursor-pointer text-sm font-medium" data-testid="select-all-stale">
              <input
                type="checkbox"
                checked={staleQuotes.length > 0 && selectedCount === staleQuotes.length}
                onChange={(e) => toggleAllStale(e.target.checked)}
                className="w-4 h-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
              />
              Select All ({staleQuotes.length})
            </label>
            <span className="text-sm text-slate-500">{selectedCount} selected</span>
          </div>
          
          <div className="overflow-y-auto flex-1 space-y-2 py-2" style={{ maxHeight: '400px' }}>
            {staleQuotes.map(quote => (
              <label
                key={quote.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedStale[quote.id] ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`stale-quote-${quote.id}`}
              >
                <input
                  type="checkbox"
                  checked={!!selectedStale[quote.id]}
                  onChange={(e) => setSelectedStale(prev => ({ ...prev, [quote.id]: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900 truncate">{quote.display_name || quote.client_name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">#{quote.order_number}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Created {new Date(quote.created_at).toLocaleDateString('en-GB')} &bull; {quote.pieces?.length || 0} piece{(quote.pieces?.length || 0) !== 1 ? 's' : ''}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-medium text-slate-700">
                    {quote.total ? `£${quote.total.toFixed(2)}` : 'No price'}
                  </div>
                  <div className="text-xs text-slate-400 capitalize">{(quote.status || '').replace(/[-_]/g, ' ')}</div>
                </div>
              </label>
            ))}
          </div>
          
          <div className="flex items-center justify-between pt-3 border-t">
            <Button variant="outline" onClick={dismissStaleDialog} data-testid="dismiss-stale-btn">
              Dismiss
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">
                Threshold adjustable in Settings
              </span>
              <Button 
                onClick={handleBulkArchive} 
                disabled={selectedCount === 0 || archiving}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="archive-selected-btn"
              >
                <Archive className="w-4 h-4 mr-2" />
                {archiving ? 'Archiving...' : `Archive ${selectedCount > 0 ? selectedCount : ''} Selected`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-4 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {hasMemorials && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/dashboard')}
                  data-testid="back-to-dashboard-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
              )}
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Worktops & Hearths</h1>
                <p className="text-gray-600 mt-1">Manage kitchen worktops and fireplace orders</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/worktops/new')}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-worktop-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Order / Quote
            </Button>
          </div>
          
          {/* Navigation Bar */}
          <div className="flex flex-wrap items-center gap-2 py-2 border-b border-gray-200">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/my-account')}
              data-testid="my-account-btn"
            >
              <User className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">My Account</span>
            </Button>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/settings')} 
              data-testid="worktops-settings-btn"
            >
              <Settings className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Settings</span>
            </Button>
            
            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'demo') && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/materials')} 
                data-testid="materials-btn"
              >
                <Palette className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Materials</span>
              </Button>
            )}
            
            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'demo') && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/users')} 
                data-testid="users-btn"
              >
                <User className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Users</span>
              </Button>
            )}
            
            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'demo') && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setStatusFilter('archived');
                  // scroll the in-page filter section into view so the user
                  // sees the archived list straight away
                  setTimeout(() => window.scrollTo({ top: 400, behavior: 'smooth' }), 50);
                }} 
                data-testid="archived-btn"
                title="Show archived worktop orders"
              >
                <Archive className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Archived</span>
              </Button>
            )}
            
            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'demo') && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/company-settings')} 
                data-testid="company-settings-btn"
              >
                <Building2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">My Company</span>
              </Button>
            )}
            
            {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'company_admin' || user?.role === 'demo') && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/landing-page-setup')} 
                className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-300 hover:from-blue-100 hover:to-cyan-100"
                data-testid="landing-page-btn"
              >
                <Globe className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Landing Page</span>
              </Button>
            )}
            
            <div className="flex-grow"></div>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={logout}
              className="text-red-600 border-red-300 hover:bg-red-50"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>

        {/* Accepted quotes needing action */}
        {orders.filter(o => o.status === 'accepted' && !o.is_archived).length > 0 && (
          <div className="mb-4 p-4 rounded-lg border border-green-300 bg-green-50 flex items-center justify-between flex-wrap gap-2" data-testid="accepted-quotes-banner">
            <p className="text-sm text-green-900">
              <strong>🎉 {orders.filter(o => o.status === 'accepted' && !o.is_archived).length} quote{orders.filter(o => o.status === 'accepted' && !o.is_archived).length > 1 ? 's' : ''} accepted</strong> — awaiting deposit request:
              {' '}{orders.filter(o => o.status === 'accepted' && !o.is_archived).map(o => `${o.order_number} (${o.client_name})`).join(', ')}
            </p>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search by order number or client name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white"
            />
          </div>
        </div>

        {/* View Toggle */}
        <div className="mb-4 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('all')}
          >
            All Active Orders
          </Button>
          <Button
            size="sm"
            variant={statusFilter === 'drafts' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('drafts')}
          >
            Drafts ({orders.filter(o => o.status === 'draft' && !o.is_archived).length})
          </Button>
          <Button
            size="sm"
            variant={statusFilter === 'quotes' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('quotes')}
            className={orders.filter(o => ['quote', 'new_quote'].includes(o.status) && (!o.total || o.total === 0) && !o.is_archived).length > 0 ? 'bg-orange-600 hover:bg-orange-700 text-white border-orange-600' : ''}
          >
            New Quote Requests ({orders.filter(o => ['quote', 'new_quote'].includes(o.status) && (!o.total || o.total === 0) && !o.is_archived).length})
          </Button>
          <Button
            size="sm"
            variant={statusFilter === 'archived' ? 'default' : 'outline'}
            onClick={() => setStatusFilter('archived')}
          >
            Archived Orders ({orders.filter(o => o.is_archived).length})
          </Button>
        </div>

        {/* Stats - Only show for non-archived view */}
        {statusFilter !== 'archived' && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <Card 
              className={`cursor-pointer hover:shadow-lg transition-shadow ${statusFilter === 'all' ? 'border-2 border-blue-500' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-blue-600">{orders.filter(o => !o.is_archived).length}</div>
              <div className="text-sm text-gray-600">Total Orders</div>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer hover:shadow-lg transition-shadow ${statusFilter === 'trade-requests' ? 'border-2 border-amber-500' : orders.filter(o => o.status === 'Trade Quote Request').length > 0 ? 'border-2 border-amber-500' : ''}`}
            onClick={() => setStatusFilter('trade-requests')}
          >
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-amber-600">
                {orders.filter(o => o.status === 'Trade Quote Request').length}
              </div>
              <div className="text-sm text-gray-600">Trade Requests</div>
              {orders.filter(o => o.status === 'Trade Quote Request').length > 0 && (
                <div className="text-xs text-amber-600 mt-1">⚠️ Needs pricing</div>
              )}
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer hover:shadow-lg transition-shadow ${statusFilter === 'completed' ? 'border-2 border-green-500' : ''}`}
            onClick={() => setStatusFilter('completed')}
          >
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-green-600">
                {orders.filter(o => o.status === 'completed' && !o.is_archived).length}
              </div>
              <div className="text-sm text-gray-600">Completed</div>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer hover:shadow-lg transition-shadow ${statusFilter === 'active' ? 'border-2 border-purple-500' : ''}`}
            onClick={() => setStatusFilter('active')}
          >
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-purple-600">
                {orders.filter(o => ['templating-booked', 'templating-done', 'stone-ordered', 'stone-fabricated', 'fitting-scheduled'].includes(o.status) && !o.is_archived).length}
              </div>
              <div className="text-sm text-gray-600">In Progress</div>
            </CardContent>
          </Card>
          <Card 
            className={`cursor-pointer hover:shadow-lg transition-shadow ${statusFilter === 'quotes' ? 'border-2 border-orange-500' : orders.filter(o => ['quote', 'new_quote'].includes(o.status) && (!o.total || o.total === 0) && !o.is_archived).length > 0 ? 'border-2 border-orange-500 bg-orange-50' : ''}`}
            onClick={() => setStatusFilter('quotes')}
          >
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-orange-600">
                {orders.filter(o => ['quote', 'new_quote'].includes(o.status) && (!o.total || o.total === 0) && !o.is_archived).length}
              </div>
              <div className="text-sm text-gray-600">New Quote Requests</div>
              {orders.filter(o => ['quote', 'new_quote'].includes(o.status) && (!o.total || o.total === 0) && !o.is_archived).length > 0 && (
                <div className="text-xs text-orange-600 mt-1 font-semibold">📋 Needs pricing</div>
              )}
            </CardContent>
          </Card>
          </div>
        )}

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-500 text-lg">No worktop orders found</p>
              <p className="text-gray-400 text-sm mt-2">Click &quot;Add Order / Quote&quot; to create your first order</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredOrders.map(order => (
              <div
                key={order.id}
                onClick={() => navigate(`/worktops/${order.id}`)}
                className="cursor-pointer group"
              >
                <Card className={`hover:shadow-lg transition-shadow ${
                  order.status === 'Trade Quote Request' 
                    ? 'border-2 border-amber-500' 
                    : (order.status === 'quote' && (!order.subtotal || order.subtotal === 0))
                      ? 'border-2 border-orange-500 bg-orange-50' 
                      : ''
                }`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          {editingNickname === order.id ? (
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <Input
                                value={nicknameValue}
                                onChange={(e) => setNicknameValue(e.target.value)}
                                className="text-2xl font-bold h-auto py-1 px-2"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveNickname(order.id);
                                  if (e.key === 'Escape') cancelEditingNickname();
                                }}
                              />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  saveNickname(order.id);
                                }}
                                className="p-1 hover:bg-green-100 rounded"
                              >
                                <Check className="w-5 h-5 text-green-600" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  cancelEditingNickname();
                                }}
                                className="p-1 hover:bg-red-100 rounded"
                              >
                                <X className="w-5 h-5 text-red-600" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <h3 className="text-2xl font-bold text-gray-900">
                                {order.display_name || order.client_name}
                              </h3>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEditingNickname(order.id, order.display_name || order.client_name);
                                }}
                                className="p-1 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Pencil className="w-4 h-4 text-gray-500" />
                              </button>
                            </div>
                          )}
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {order.status.replace('-', ' ').toUpperCase()}
                          </span>
                          {order.status === 'quote' && (!order.subtotal || order.subtotal === 0) && (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-400">
                              📋 NEW QUOTE REQUEST
                            </span>
                          )}
                          {order.status === 'Trade Quote Request' && (
                            <span className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
                              🔔 ACTION REQUIRED
                            </span>
                          )}
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                            {order.order_type}
                          </span>
                          {order.trade_discount > 0 && (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                              Trade Customer
                            </span>
                          )}
                        </div>
                        <div className="text-gray-600 text-sm">
                          <span className="font-medium">Order #{order.order_number}</span>
                          {order.client_phone && <span className="ml-4">📞 {order.client_phone}</span>}
                          {order.client_email && <span className="ml-4">✉ {order.client_email}</span>}
                        </div>
                        <div className="text-sm text-gray-500 mt-2">
                          {order.pieces?.length || 0} piece{(order.pieces?.length || 0) !== 1 ? 's' : ''} • 
                          Created {new Date(order.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold text-blue-600">
                          Balance: £{((order.total || 0) - (order.total_paid || 0)).toFixed(2)}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          Total: £{(order.total || 0).toFixed(2)} <span className="text-xs text-gray-500">(inc. VAT)</span>
                        </div>
                        {order.trade_discount > 0 && (
                          <div className="text-xs text-green-600 mt-1">{order.trade_discount}% trade discount</div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default WorktopsDashboard;